import { Component, OnInit, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AuthService, CurrentUser } from '../core/services/auth.service';
import { environment } from '../../environments/environment';

const PROD_HIDDEN_ROUTES = ['/reservations', '/work-orders', '/inspections', '/schedule', '/reports', '/payments', '/insights'];
import { ToastComponent } from '../shared/components/toast/toast.component';
import { ConfirmDialogComponent } from '../shared/components/confirm-dialog/confirm-dialog.component';

interface NavItem { label: string; icon: string; route: string; roles?: string[]; group: string; }

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ToastComponent, ConfirmDialogComponent],
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss']
})
export class ShellComponent implements OnInit {
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  router = inject(Router);

  searchQuery = '';
  searchResults: any = null;
  showResults = false;
  searching = false;
  private searchTimer: any;

  navItems: NavItem[] = [
    { label: 'Dashboard',    icon: '📊', route: '/dashboard',    group: 'main' },
    { label: 'Properties',   icon: '🏢', route: '/portfolio',    group: 'main' },
    { label: 'List Groups',  icon: '📋', route: '/worklists',    group: 'main' },
    { label: 'Tasks',        icon: '✅', route: '/tasks',        group: 'ops'  },
    { label: 'Reservations', icon: '🛎️', route: '/reservations', group: 'ops'  },
    { label: 'Work Orders',  icon: '🔧', route: '/work-orders',  group: 'ops'  },
    { label: 'Inspections',  icon: '🔍', route: '/inspections',  group: 'ops'  },
    { label: 'Schedule',     icon: '📅', route: '/schedule',     group: 'ops'  },
    { label: 'Messages',     icon: '💬', route: '/messages',     group: 'ops'  },
    { label: 'Staff',        icon: '👥', route: '/workforce',    roles: ['Owner','Manager','Admin','CompanyAdmin','SuperAdmin'], group: 'mgmt' },
    { label: 'Reports',      icon: '📈', route: '/reports',      roles: ['Owner','Manager','Admin','CompanyAdmin','SuperAdmin'], group: 'mgmt' },
    { label: 'Payments',     icon: '💳', route: '/payments',     roles: ['Owner','Manager','Admin','CompanyAdmin','SuperAdmin'], group: 'mgmt' },
    { label: 'Insights',     icon: '📉', route: '/insights',     roles: ['Owner','Manager','Admin','CompanyAdmin','SuperAdmin'], group: 'mgmt' },
    { label: 'Tenants',      icon: '🏛️', route: '/tenants',      roles: ['SuperAdmin'], group: 'admin' },
      { label: 'Settings',     icon: '⚙️',  route: '/settings',     roles: ['Owner','Manager','Admin','CompanyAdmin','SuperAdmin'], group: 'admin' },
  ];

  visibleMain:  NavItem[] = [];
  visibleOps:   NavItem[] = [];
  visibleMgmt:  NavItem[] = [];
  visibleAdmin: NavItem[] = [];
  sidebarOpen = typeof window !== 'undefined' ? window.innerWidth > 768 : true;

  notifOpen = false;
  notifications = [
    { id: '1', icon: '✅', text: 'Task "Sunset Villa Turnover" completed by Maria G.', time: '2 min ago', read: false },
    { id: '2', icon: '🔧', text: 'Work Order #WO-041 escalated to High priority', time: '18 min ago', read: false },
    { id: '3', icon: '🔍', text: 'Inspection at Beach House Blue scored 58% — review required', time: '1 hr ago', read: true },
    { id: '4', icon: '💬', text: 'New message from Tom R. (Vendor)', time: '2 hr ago', read: true },
  ];
  get unreadNotifications() { return this.notifications.filter(n => !n.read).length; }

  currentUser$ = this.authService.currentUser$;
  get currentUser() { return this.authService.currentUser; }
  get isSuperAdmin() { return this.authService.isSuperAdmin; }
  get initials(): string {
    return this.currentUser?.fullName?.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) ?? 'U';
  }

  ngOnInit() {
    this.currentUser$.subscribe((user: CurrentUser | null) => {
      const userRoles = user
        ? Array.isArray(user.roles) && user.roles.length > 0
          ? user.roles.map((r: string) => r.toLowerCase())
          : [(user.role ?? '').toLowerCase()]
        : [];
      const filtered = (user
        ? this.navItems.filter(n => !n.roles || n.roles.some(r => userRoles.includes(r.toLowerCase())))
        : this.navItems
      ).filter(n => !environment.production || !PROD_HIDDEN_ROUTES.includes(n.route));
      this.visibleMain  = filtered.filter(n => n.group === 'main');
      this.visibleOps   = filtered.filter(n => n.group === 'ops');
      this.visibleMgmt  = filtered.filter(n => n.group === 'mgmt');
      this.visibleAdmin = filtered.filter(n => n.group === 'admin');
    });
  }

  onNavItemClick(item: NavItem) {
    // Mobile: always close after navigation
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.sidebarOpen = false;
    }
    // Settings route: always collapse sidebar
    if (item.route === '/settings') {
      this.sidebarOpen = false;
    }
  }

  logout() { this.authService.logout(); this.router.navigate(['/auth/login']); }

  onSearchInput() {
    clearTimeout(this.searchTimer);
    if (!this.searchQuery.trim() || this.searchQuery.trim().length < 2) {
      this.searchResults = null;
      this.showResults = false;
      this.searching = false;
      return;
    }
    this.searching = true;
    this.showResults = true;
    this.searchTimer = setTimeout(() => this.runGlobalSearch(), 300);
  }

  onSearchFocus() {
    if (this.searchResults && this.searchQuery.trim().length >= 2) {
      this.showResults = true;
    }
  }

  private runGlobalSearch() {
    const q = this.searchQuery.trim();
    if (q.length < 2) return;
    this.http.get<any>(`${environment.apiUrl}/search?q=${encodeURIComponent(q)}`).subscribe({
      next: (r) => { this.searchResults = r; this.searching = false; },
      error: () => { this.searching = false; }
    });
  }

  clearSearch() {
    this.searchQuery = '';
    this.searchResults = null;
    this.showResults = false;
    this.searching = false;
    clearTimeout(this.searchTimer);
  }

  closeSearch() { this.showResults = false; }

  hasResults(): boolean {
    if (!this.searchResults) return false;
    return (
      (this.searchResults.tasks?.length ?? 0) +
      (this.searchResults.workOrders?.length ?? 0) +
      (this.searchResults.inspections?.length ?? 0) +
      (this.searchResults.reservations?.length ?? 0) +
      (this.searchResults.properties?.length ?? 0) +
      (this.searchResults.staff?.length ?? 0) +
      (this.searchResults.workLists?.length ?? 0)
    ) > 0;
  }

  navigateTo(hit: any) {
    this.closeSearch();
    switch (hit.type) {
      case 'Task':        this.router.navigate(['/tasks', hit.id]); break;
      case 'WorkOrder':   this.router.navigate(['/work-orders']); break;
      case 'Inspection':  this.router.navigate(['/inspections', hit.id]); break;
      case 'Reservation': this.router.navigate(['/reservations']); break;
      case 'Property':    this.router.navigate(['/portfolio', hit.id]); break;
      case 'Staff':       this.router.navigate(['/workforce', hit.id]); break;
      case 'WorkList':    this.router.navigate(['/worklists', hit.id]); break;
    }
  }

  toggleNotifications(event: Event) { event.stopPropagation(); this.notifOpen = !this.notifOpen; }
  markRead(n: any) { n.read = true; }
  clearNotifications() { this.notifications = []; this.notifOpen = false; }

  isFullscreen = !!document.fullscreenElement;

  toggleFullscreen(event: Event) {
    event.stopPropagation();
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange() { this.isFullscreen = !!document.fullscreenElement; }

  @HostListener('document:click')
  onDocClick() { this.notifOpen = false; this.showResults = false; }
}